import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Medication as RxIcon } from '@mui/icons-material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';

interface Prescription {
  id: number;
  prescription_number: string;
  prescription_date: string;
  status: string;
  diagnosis: string | null;
  refills_remaining: number | null;
  expiry_date: string | null;
  pet_name: string;
  vet_name: string | null;
}

const statusColor = (s: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  if (s === 'Active') return 'success';
  if (s === 'Dispensed') return 'info';
  if (s === 'Expired' || s === 'Cancelled') return 'error';
  return 'default';
};

const PortalRefillsPage = () => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Refill dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRx, setSelectedRx] = useState<Prescription | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    portalApi.get<{ data: Prescription[] }>(API_ENDPOINTS.PORTAL.PRESCRIPTIONS)
      .then(res => setPrescriptions(res.data ?? []))
      .catch(() => setError('Failed to load prescriptions'))
      .finally(() => setLoading(false));
  }, []);

  const openRefill = (rx: Prescription) => {
    setSelectedRx(rx); setNotes(''); setSubmitError(''); setSuccessMsg(''); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedRx) return;
    setSubmitting(true); setSubmitError('');
    try {
      const res = await portalApi.post<{ message: string }>(
        API_ENDPOINTS.PORTAL.REFILL(selectedRx.id), { notes: notes || undefined }
      );
      setSuccessMsg(res.message ?? 'Refill request submitted successfully.');
      setDialogOpen(false);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit refill request');
    } finally {
      setSubmitting(false);
    }
  };

  const canRefill = (rx: Prescription) =>
    (rx.status === 'Active' || rx.status === 'Dispensed') &&
    (rx.refills_remaining === null || rx.refills_remaining > 0);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RxIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Prescriptions & Refills</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Prescription #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Pet</TableCell>
              <TableCell>Veterinarian</TableCell>
              <TableCell>Diagnosis</TableCell>
              <TableCell>Refills Left</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {prescriptions.map(rx => (
              <TableRow key={rx.id} hover>
                <TableCell><strong>{rx.prescription_number}</strong></TableCell>
                <TableCell>{rx.prescription_date}</TableCell>
                <TableCell>{rx.pet_name}</TableCell>
                <TableCell>{rx.vet_name || '-'}</TableCell>
                <TableCell>{rx.diagnosis || '-'}</TableCell>
                <TableCell>
                  {rx.refills_remaining === null ? 'Unlimited' : rx.refills_remaining}
                </TableCell>
                <TableCell>{rx.expiry_date || '-'}</TableCell>
                <TableCell><Chip label={rx.status} size="small" color={statusColor(rx.status)} /></TableCell>
                <TableCell>
                  <Button size="small" variant="outlined"
                    disabled={!canRefill(rx)}
                    onClick={() => openRefill(rx)}>
                    Request Refill
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {prescriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">No prescriptions found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Refill Request Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request Refill</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedRx && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Prescription <strong>{selectedRx.prescription_number}</strong> for <strong>{selectedRx.pet_name}</strong>
            </Typography>
          )}
          <TextField fullWidth size="small" label="Notes for the clinic (optional)"
            multiline rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
          {submitError && <Alert severity="error" sx={{ mt: 1 }}>{submitError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalRefillsPage;
